// BrimLight/client/helpers/video-editor/KenBurnsAnimator.js

/**
 * KenBurnsAnimator
 * Professional image animation system for video editing
 * Implements industry-standard Ken Burns effects with proper timing and easing
 */
export class KenBurnsAnimator {
    /**
     * Animation presets with keyframe definitions
     * All movements are subtle and professional
     */
    static effects = {
        static: null, // No animation

        // Basic zoom effects
        ken_burns_zoom_in: [
            { transform: 'scale(1)', offset: 0 },
            { transform: 'scale(1.15)', offset: 1 }
        ],

        ken_burns_zoom_out: [
            { transform: 'scale(1.15)', offset: 0 },
            { transform: 'scale(1)', offset: 1 }
        ],

        // Pure pan effects (with higher zoom to avoid black edges)
        ken_burns_pan_right: [
            { transform: 'scale(1.25) translateX(-3%)', offset: 0 },
            { transform: 'scale(1.25) translateX(3%)', offset: 1 }
        ],

        ken_burns_pan_left: [
            { transform: 'scale(1.25) translateX(3%)', offset: 0 },
            { transform: 'scale(1.25) translateX(-3%)', offset: 1 }
        ],

        ken_burns_pan_up: [
            { transform: 'scale(1.25) translateY(3%)', offset: 0 },
            { transform: 'scale(1.25) translateY(-3%)', offset: 1 }
        ],

        ken_burns_pan_down: [
            { transform: 'scale(1.25) translateY(-3%)', offset: 0 },
            { transform: 'scale(1.25) translateY(3%)', offset: 1 }
        ],

        // Combined zoom + pan effects (more cinematic)
        ken_burns_zoom_pan_right: [
            { transform: 'scale(1.1) translateX(-2.5%)', offset: 0 },
            { transform: 'scale(1.25) translateX(2.5%)', offset: 1 }
        ],

        ken_burns_zoom_pan_left: [
            { transform: 'scale(1.1) translateX(2.5%)', offset: 0 },
            { transform: 'scale(1.25) translateX(-2.5%)', offset: 1 }
        ],

        ken_burns_zoom_pan_up: [
            { transform: 'scale(1.1) translateY(2.5%)', offset: 0 },
            { transform: 'scale(1.25) translateY(-2.5%)', offset: 1 }
        ],

        ken_burns_zoom_pan_down: [
            { transform: 'scale(1.1) translateY(-2.5%)', offset: 0 },
            { transform: 'scale(1.25) translateY(2.5%)', offset: 1 }
        ],

        // Reveal effects (zoom out while panning)
        ken_burns_reveal_right: [
            { transform: 'scale(1.25) translateX(-2%)', offset: 0 },
            { transform: 'scale(1.1) translateX(2%)', offset: 1 }
        ],

        ken_burns_reveal_left: [
            { transform: 'scale(1.25) translateX(2%)', offset: 0 },
            { transform: 'scale(1.1) translateX(-2%)', offset: 1 }
        ],
    };

    /**
     * Apply an animation effect to an image element
     * @param {HTMLElement} imageElement - The image element to animate
     * @param {string} effectName - Name of the effect to apply
     * @param {number} duration - Duration in seconds
     * @param {Object} options - Additional options
     * @returns {Animation|null} - Web Animation API animation object
     */
    static applyEffect(imageElement, effectName = 'ken_burns_zoom_in', duration = 5, options = {}) {
        // Handle static (no animation)
        if (effectName === 'static' || !effectName) {
            // Remove pan-effect class if present
            imageElement.classList.remove('pan-effect');
            return null;
        }

        // Get keyframes for the effect
        const keyframes = this.effects[effectName];
        if (!keyframes) {
            console.warn(`Unknown effect: ${effectName}, falling back to ken_burns_zoom_in`);
            return this.applyEffect(imageElement, 'ken_burns_zoom_in', duration, options);
        }

        // --- FIX: Add pan-effect class for effects that need larger image size ---
        const isPanEffect = effectName.includes('pan') || effectName.includes('reveal');
        if (isPanEffect) {
            imageElement.classList.add('pan-effect');
        } else {
            imageElement.classList.remove('pan-effect');
        }

        // Animation options
        const animationOptions = {
            duration: duration * 1000, // Convert to milliseconds
            easing: options.easing || 'ease-in-out', // Smooth acceleration and deceleration
            fill: 'forwards', // Hold the end state
            iterations: 1, // Play once, no looping
            ...options
        };

        // Apply the animation using Web Animations API
        const animation = imageElement.animate(keyframes, animationOptions);

        return animation;
    }

    /**
     * Get a smart default effect based on clip and image characteristics
     * @param {Object} clipData - Clip data from the database
     * @param {Object} imageData - Optional image metadata (dimensions, etc.)
     * @returns {string} - Recommended effect name
     */
    static getRecommendedEffect(clipData, imageData = {}) {
        if (clipData?.imageEffect && clipData.imageEffect !== 'auto') {
            return clipData.imageEffect;
        }


        // Smart defaults based on context
        const clipType = clipData?.type;
        const hasCharacters = clipData?.characterIds?.length > 0;
        const hasEnvironment = clipData?.environmentId;
        const duration = clipData?.duration || 5;

        // Character-focused clips: subtle zoom in to draw attention
        if (hasCharacters && clipType === 'visual') {
            return 'ken_burns_zoom_in';
        }

        // Environment/establishing shots: reveal with zoom out
        if (hasEnvironment && !hasCharacters) {
            return 'ken_burns_zoom_out';
        }

        // Longer clips: more movement (pan + zoom)
        if (duration > 8) {
            const movements = [
                'ken_burns_zoom_pan_right',
                'ken_burns_zoom_pan_left',
                'ken_burns_pan_right',
                'ken_burns_pan_left'
            ];
            // Random but consistent based on clip ID
            const index = clipData?._id ?
                parseInt(clipData._id.slice(-4), 16) % movements.length :
                Math.floor(Math.random() * movements.length);
            return movements[index];
        }

        // Shorter clips: simple zoom in (safest choice)
        if (duration <= 3) {
            return 'ken_burns_zoom_in';
        }

        // Medium duration: alternate between zoom in and pan
        return Math.random() > 0.5 ? 'ken_burns_zoom_in' : 'ken_burns_pan_right';
    }

    /**
     * Cancel an ongoing animation
     * @param {Animation} animation - The animation object to cancel
     */
    static cancelAnimation(animation) {
        if (animation && typeof animation.cancel === 'function') {
            animation.cancel();
            
            // --- FIX: Remove pan-effect class when canceling ---
            if (animation.effect?.target) {
                animation.effect.target.classList.remove('pan-effect');
            }
        }
    }

    /**
     * Get list of all available effects
     * @returns {Array<string>} - Array of effect names
     */
    static getAvailableEffects() {
        return Object.keys(this.effects);
    }

    /**
     * Get display-friendly name for an effect
     * @param {string} effectName - Internal effect name
     * @returns {string} - Human-readable name
     */
    static getEffectDisplayName(effectName) {
        const displayNames = {
            static: 'Static (No Movement)',
            ken_burns_zoom_in: 'Zoom In (Subtle)',
            ken_burns_zoom_out: 'Zoom Out (Reveal)',
            ken_burns_pan_right: 'Pan Right',
            ken_burns_pan_left: 'Pan Left',
            ken_burns_pan_up: 'Pan Up',
            ken_burns_pan_down: 'Pan Down',
            ken_burns_zoom_pan_right: 'Zoom & Pan Right',
            ken_burns_zoom_pan_left: 'Zoom & Pan Left',
            ken_burns_zoom_pan_up: 'Zoom & Pan Up',
            ken_burns_zoom_pan_down: 'Zoom & Pan Down',
            ken_burns_reveal_right: 'Reveal Right',
            ken_burns_reveal_left: 'Reveal Left'
        };
        return displayNames[effectName] || effectName;
    }

    /**
     * Preview effect parameters (useful for UI)
     * @param {string} effectName - Effect to preview
     * @returns {Object} - Effect metadata
     */
    static getEffectMetadata(effectName) {
        const metadata = {
            static: { movement: 'none', intensity: 0, recommended: 'Any scene' },
            ken_burns_zoom_in: { movement: 'zoom', intensity: 1, recommended: 'Character focus, portraits' },
            ken_burns_zoom_out: { movement: 'zoom', intensity: 1, recommended: 'Establishing shots, reveals' },
            ken_burns_pan_right: { movement: 'horizontal', intensity: 2, recommended: 'Wide landscapes' },
            ken_burns_pan_left: { movement: 'horizontal', intensity: 2, recommended: 'Wide landscapes' },
            ken_burns_pan_up: { movement: 'vertical', intensity: 2, recommended: 'Tall subjects, buildings' },
            ken_burns_pan_down: { movement: 'vertical', intensity: 2, recommended: 'Tall subjects, buildings' },
            ken_burns_zoom_pan_right: { movement: 'combined', intensity: 3, recommended: 'Long clips, cinematic' },
            ken_burns_zoom_pan_left: { movement: 'combined', intensity: 3, recommended: 'Long clips, cinematic' },
            ken_burns_zoom_pan_up: { movement: 'combined', intensity: 3, recommended: 'Long clips, cinematic' },
            ken_burns_zoom_pan_down: { movement: 'combined', intensity: 3, recommended: 'Long clips, cinematic' },
            ken_burns_reveal_right: { movement: 'reveal', intensity: 2, recommended: 'Story reveals' },
            ken_burns_reveal_left: { movement: 'reveal', intensity: 2, recommended: 'Story reveals' }
        };
        return metadata[effectName] || { movement: 'unknown', intensity: 1, recommended: 'General use' };
    }
}